import { ATMOSPHERE_PALETTE as P } from '../palette';
import { forestBiome } from '../biomeArchetypes';
import { TIDEWEND_SILHOUETTES } from '../../../game/effects/atmosphere/silhouettes/coilParallaxSilhouettes';
import { COASTAL_TOWN_SILHOUETTES } from '../../../game/effects/atmosphere/silhouettes/coastalTownSilhouettes';
import { multiPlaneParallax } from '../presetBuilders';
import type { AtmospherePreset } from '../presets.config';

/** Tidewend — the Hollow Coil's moonlit harbor town. Cool violet night owns
 *  the sky and rooftops while warm lantern pools, windows, and hearths keep the
 *  inhabited streets readable. The Coil fog has reached the eastern watch but
 *  not the market: safe and alive, with darkness visibly waiting outside. */
export const TIDEWEND_SHARED_BED = forestBiome({
  id: 'tidewend',
  sky: { from: 0x17152e, to: 0x38314f },
  celestial: { atX: 0.78, atY: 0.20, color: 0xe2e5ff, haloColor: 0x8e92c8, size: 72, haloScale: 4.2, coreAlpha: 0.82, haloAlpha: 0.20, bodyType: 'moon', moonPhase: 'gibbous' },
  cloudBands: { count: 5, color: 0x77708f, sizeMin: 420, sizeMax: 760, alphaMin: 0.05, alphaMax: 0.12, driftPxPerSec: 4, bandYMin: 0.12, bandYMax: 0.42 },
  motes: { colors: [P.firefly, P.amber, P.torch], driftPx: 36 },
  rays: { color: 0xaab3df, tiltDeg: -12, height: 620, alphaMin: 0.06, alphaMax: 0.20 },
  glow: {
    blobs: [
      // Several small warm lantern pools at village-light heights.
      { color: P.torch, atX: 0.18, atY: 0.62, size: 180, alpha: 0.10 },
      { color: P.torch, atX: 0.42, atY: 0.68, size: 160, alpha: 0.09 },
      { color: P.torch, atX: 0.66, atY: 0.60, size: 200, alpha: 0.10 },
      { color: P.torch, atX: 0.84, atY: 0.72, size: 170, alpha: 0.09 },
      // Low warm hearth glow on the floor.
      { color: P.amber, atX: 0.5, atY: 0.96, size: 580, alpha: 0.11 },
    ],
    breathe: { periodMs: 5200, min: 0.80, max: 1.25 },
  },
  lightField: { palette: [0x77719b, P.firefly, P.torch] },
  weather: { mode: 'fall', color: P.amber, colorEnd: 0x8a2418, size: 12, frequencyMs: 160, lifespanMs: 5200, speed: 14, gravityY: 8, windX: 28, alphaEnd: 0.55, gust: { periodMs: 8000, min: 0.5, max: 1.4, phase: 0.3 } },
  flicker: { color: P.torch, size: 220, baseAlpha: 0.08, flickerAmp: 0.06, hzMin: 2, hzMax: 5, anchors: [{ atX: 0.18, atY: 0.62 }, { atX: 0.42, atY: 0.68 }, { atX: 0.66, atY: 0.60 }, { atX: 0.84, atY: 0.72 }] },
  parallax: [
    // Authored silhouettes (Coil art pack): gabled village skyline + tree
    // canopies, darker roofline, dock-pylon foreground. Deliberately NO
    // lighthouse — the village faces the sea; the absence is the story beat.
    { shape: 'spires', color: 0x302b45, speed: 0.04, height: 300, alpha: 0.74, detail: 0.8, sway: { ampPx: 8, periodMs: 30000 }, svg: TIDEWEND_SILHOUETTES.far, occludeCelestial: true },
    { shape: 'ridge', color: 0x211d35, speed: 0.10, height: 220, alpha: 0.86, detail: 1.0, svg: TIDEWEND_SILHOUETTES.mid },
    { shape: 'peaks', color: 0x151322, speed: 0.18, height: 150, alpha: 0.96, detail: 1.0, svg: TIDEWEND_SILHOUETTES.near },
  ],
  // A wisp of fog at the treeline — the fog-as-antagonist through-line's first
  // appearance (absent on the beach → a wisp here → heavy on the moor → pooled
  // in the undercroft). Very faint, warm-neutral, high at the treeline.
  fog: { color: 0x9aa0bd, bands: 4, alphaMin: 0.04, alphaMax: 0.10, driftPx: 36, periodMinMs: 14000, periodMaxMs: 24000, yMin: 0.34, yMax: 0.66, sizeMin: 220, sizeMax: 440 },
  closer: {
    // Un-graded and un-vignetted: preserves crisp night-sky contrast without an atmospheric rectangle veil.
  },
});

const COASTAL_PLANES = multiPlaneParallax([
  { shape: 'ridge', color: 0x41435e, speed: 0.025, height: 360, alpha: 0.82, svg: COASTAL_TOWN_SILHOUETTES.headlands, occludeCelestial: true },
  { shape: 'ridge', color: 0x2d3651, speed: 0.045, height: 200, alpha: 0.72, svg: COASTAL_TOWN_SILHOUETTES.water },
  { shape: 'ridge', color: 0x211f35, speed: 0.12, height: 240, alpha: 0.88, svg: COASTAL_TOWN_SILHOUETTES.quays },
]);

/** Main-town panorama only. The original bed remains intact for the port and
 * outskirts. No first-view landmarks, ambient animation or new layer owner. */
export const TIDEWEND: AtmospherePreset = {
  ...TIDEWEND_SHARED_BED,
  layers: TIDEWEND_SHARED_BED.layers.map(layer => layer.kind === 'parallax'
    ? COASTAL_PLANES.find(plane => plane.depth === layer.depth) ?? layer : layer),
};
